import React, { useState, useEffect } from 'react';
import axios from 'axios';
import StudentRegistrationPage from './components/StudentRegistrationPage';
import TeacherRegistrationPage from './components/TeacherRegistrationPage';
import { initializeAnalytics, trackEvent, trackStepView } from './lib/analytics';

const offeredTeacherRoles = new Set([
  'Principal/ HOS / HM / School Head',
  'Head Teacher/Incharge Head Teacher',
  'Nodal Teacher',
  'Teacher',
  'Govt Nodal Officer',
  'Mentor Teachers',
  'Zonal Coordinators',
  'Deputy Director Education',
  'District Coordinator'
]);

const normalizeTeacherRoleForUpdate = (role) =>
  offeredTeacherRoles.has(role) ? role : 'Nodal Teacher';

const teacherStepNames = {
  1: 'personal_details',
  2: 'school_location',
  3: 'school_details',
  4: 'language_and_role',
  5: 'review_and_consent',
  6: 'submission_status'
};

function TeacherRegistrationApp() {
  const [step, setStep] = useState(1);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [registrationMessage, setRegistrationMessage] = useState('');
  const [isExistingTeacher, setIsExistingTeacher] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);
  const [stateDistricts, setStateDistricts] = useState([]);
  const [isLoadingStateDistricts, setIsLoadingStateDistricts] = useState(true);
  const [allSchoolDetails, setAllSchoolDetails] = useState([]);
  const [isLoadingSchoolDetails, setIsLoadingSchoolDetails] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    phone: '',
    confirmPhone: '',
    state: '',
    district: '',
    grade: '',
    section: '',
    city: '',
    department: '',
    school: '',
    role: '',
    language: '',
    agreeTerms: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
  const apiKey = process.env.REACT_APP_API_KEY || '';

  useEffect(() => {
    const controller = new AbortController();

    const loadStateDistricts = async () => {
      setIsLoadingStateDistricts(true);

      try {
        const response = await axios.post(
          `${apiBaseUrl}/api/method/tap_lms.onboarding.teacher_registration.list_state_districts`,
          { api_key: apiKey },
          { signal: controller.signal }
        );
        const payload = response.data?.message || response.data || {};

        setStateOptions(Array.isArray(payload.states) ? payload.states : []);
        setStateDistricts(Array.isArray(payload.districts) ? payload.districts : []);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          console.error('[Location Directory] list_state_districts failed:', error);
          setStateOptions([]);
          setStateDistricts([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingStateDistricts(false);
        }
      }
    };

    loadStateDistricts();

    return () => controller.abort();
  }, [apiBaseUrl, apiKey]);

  useEffect(() => {
    const stateName = formData.state;
    const districtName = formData.district;

    if (!stateName || !districtName) {
      setAllSchoolDetails([]);
      setIsLoadingSchoolDetails(false);
      return undefined;
    }

    const controller = new AbortController();

    const loadSchoolDirectory = async () => {
      setAllSchoolDetails([]);
      setIsLoadingSchoolDetails(true);

      try {
        const response = await axios.post(
          `${apiBaseUrl}/api/method/tap_lms.onboarding.teacher_registration.list_school_details`,
          {
            api_key: apiKey,
            state_name: stateName,
            district_name: districtName
          },
          { signal: controller.signal }
        );

        const schools =
          response.data?.schools ||
          response.data?.message?.schools ||
          response.data?.message?.data ||
          response.data?.message ||
          [];

        if (Array.isArray(schools)) {
          setAllSchoolDetails(schools);
        } else {
          console.warn('[School Directory] Unexpected response:', response.data);
          setAllSchoolDetails([]);
        }
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          console.error('[School Directory] list_school_details failed:', error);
          setAllSchoolDetails([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingSchoolDetails(false);
        }
      }
    };

    loadSchoolDirectory();

    return () => controller.abort();
  }, [apiBaseUrl, apiKey, formData.state, formData.district]);

  useEffect(() => {
    trackStepView({
      flow: 'teacher_registration',
      step,
      stepName: teacherStepNames[step] || `step_${step}`,
      pagePath: `/teacher/step-${step}`,
      pageTitle: `Teacher Registration Step ${step}`,
      status: step === 6 ? registrationStatus || 'completed' : 'in_progress'
    });
  }, [step, registrationStatus]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const checkTeacherExists = async () => {
    if (isSubmitting) return false;
    setIsSubmitting(true);

    try {
      const phone = formData.phone;

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL || ''}/api/method/tap_lms.onboarding.teacher_registration.check_teacher_exists`,
        { phone }
      );

      const result = response.data?.message || response.data || {};
      if (result.exists || result.status === 'already_registered') {
        const teacherDetails = await getTeacherDetails(phone);
        hydrateExistingTeacher(teacherDetails);
        setIsExistingTeacher(true);
        trackEvent('registration_existing_user_detected', {
          flow: 'teacher_registration'
        });
        setStep(5);
        return false;
      }

      setIsExistingTeacher(false);
      return true;
    } catch (error) {
      console.error('[Teacher Registration] check_teacher_exists failed:', error);
      setRegistrationStatus('error');
      setRegistrationMessage('Unable to verify teacher details. Please try again later.');
      setStep(6);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTeacherDetails = async (phone) => {
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL || ''}/api/method/tap_lms.onboarding.teacher_registration.get_teacher_details`,
      { phone }
    );
    return response.data?.message || response.data || {};
  };

  const hydrateExistingTeacher = (teacherDetails) => {
    const fullName =
      teacherDetails.name ||
      teacherDetails.teacher_name ||
      formData.name;

    const nextFormData = {
      name: fullName,
      phone:
        String(
          teacherDetails.phone ||
          teacherDetails.mobile ||
          teacherDetails.whatsapp_number ||
          formData.phone
        ).replace(/^91/, ''),
      confirmPhone:
        String(
          teacherDetails.phone ||
          teacherDetails.mobile ||
          teacherDetails.whatsapp_number ||
          formData.phone
        ).replace(/^91/, ''),
      gender: teacherDetails.gender || formData.gender,
      state: teacherDetails.state || formData.state,
      district: teacherDetails.district || formData.district,
      grade: teacherDetails.grade || formData.grade,
      section: teacherDetails.section || formData.section,
      city: teacherDetails.city || formData.city,
      department: teacherDetails.department || formData.department,
      school:
        teacherDetails.school ||
        teacherDetails.school_name ||
        teacherDetails.School_name ||
        formData.school,
      role: teacherDetails.role || formData.role,
      language: teacherDetails.language || formData.language,
      agreeTerms: formData.agreeTerms
    };

    setFormData((prev) => ({
      ...prev,
      ...nextFormData
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setRegistrationStatus(null);
    setRegistrationMessage('');

    try {
      const normalizedName = formData.name.trim().replace(/\s+/g, ' ');
      const createTeacherPayload = {
        api_key: apiKey,
        name: normalizedName,
        gender: formData.gender,
        phone: formData.phone,
        state: formData.state,
        grade: formData.grade,
        section: formData.section.trim(),
        school: formData.school,
        role: formData.role,
        language: formData.language
      };

      if (isExistingTeacher) {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL || ''}/api/method/tap_lms.onboarding.teacher_registration.update_teacher_details`,
          {
            phone: formData.phone,
            name: normalizedName,
            school: formData.school,
            grade: formData.grade,
            section: formData.section.trim(),
            role: normalizeTeacherRoleForUpdate(formData.role),
            language: formData.language
          }
        );
        setRegistrationStatus('success');
        setRegistrationMessage(
          response.data?.message?.message ||
          response.data?.message ||
          'Teacher details updated successfully.'
        );
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL || ''}/api/method/tap_lms.onboarding.teacher_registration.create_teacher_web`,
          createTeacherPayload
        );
        setRegistrationStatus('success');
        setRegistrationMessage(
          response.data?.message?.message ||
          response.data?.message ||
          'Teacher registered successfully.'
        );
      }
    } catch (error) {
      console.error('[Teacher Registration] submit failed:', error);
      setRegistrationStatus('error');
      setRegistrationMessage(
        error.response?.data?.message?.message ||
        error.response?.data?.message ||
        'Teacher registration failed.'
      );
    } finally {
      setIsSubmitting(false);
      setStep(6);
    }
  };

  return (
    <TeacherRegistrationPage
      step={step}
      setStep={setStep}
      formData={formData}
      setFormData={setFormData}
      stateOptions={stateOptions}
      stateDistricts={stateDistricts}
      isLoadingStateDistricts={isLoadingStateDistricts}
      allSchoolDetails={allSchoolDetails}
      isLoadingSchoolDetails={isLoadingSchoolDetails}
      checkTeacherExists={checkTeacherExists}
      handleInputChange={handleInputChange}
      handleSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      registrationStatus={registrationStatus}
      registrationMessage={registrationMessage}
      isExistingTeacher={isExistingTeacher}
    />
  );
}

function App() {
  const currentPath = window.location.pathname;
  const studentPathMatch = currentPath.match(/^\/student\/([^/]+)$/);

  useEffect(() => {
    initializeAnalytics();
  }, []);

  if (studentPathMatch) {
    const schoolId = decodeURIComponent(studentPathMatch[1]);
    return (
      <StudentRegistrationPage schoolId={schoolId} />
    );
  }

  return <TeacherRegistrationApp />;
}

export default App;
